import { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { authService } from './authService';

let isRefreshing = false;
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

/**
 * Check if token is about to expire (within 2 minutes)
 */
const isTokenExpiringSoon = (): boolean => {
  const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
  if (!tokenExpiresAt) return false;

  const expiryTime = new Date(tokenExpiresAt).getTime();
  const now = Date.now();
  const twoMinutes = 2 * 60 * 1000;

  return (expiryTime - now) < twoMinutes;
};

/**
 * Check if session has expired (8 hour sliding window)
 */
const isSessionExpired = (): boolean => {
  const sessionExpiresAt = localStorage.getItem('sessionExpiresAt');
  const anonymousExpiresAt = localStorage.getItem('anonymousExpiresAt');

  const expiresAt = sessionExpiresAt || anonymousExpiresAt;
  if (!expiresAt) return false;

  const expiryTime = new Date(expiresAt).getTime();
  const now = Date.now();

  return now >= expiryTime;
};

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: any, token: string | null = null) => {
  failedRequestsQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedRequestsQueue = [];
};

/**
 * Setup axios interceptors for automatic token refresh
 */
export const setupAuthInterceptor = (axiosInstance: AxiosInstance) => {
  // Request interceptor: refresh token if expiring soon
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Skip token refresh for auth endpoints
      if (
        config.url?.includes('/auth/otp/request') ||
        config.url?.includes('/auth/otp/verify') ||
        config.url?.includes('/auth/token/refresh') ||
        config.url?.includes('/auth/anonymous/session')
      ) {
        return config;
      }

      // Check if session has expired (12 hours of inactivity)
      if (isSessionExpired()) {
        console.warn('Session expired - redirecting to login');
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired'));
      }

      // Check if we have a refresh token and token is expiring soon
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken && isTokenExpiringSoon() && !isRefreshing) {
        isRefreshing = true;

        try {
          console.log('Token expiring soon - refreshing proactively');
          await authService.refreshToken();
          isRefreshing = false;

          // Update Authorization header with new token
          const newToken = localStorage.getItem('accessToken');
          if (newToken && config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
        } catch (error) {
          isRefreshing = false;
          console.error('Failed to refresh token proactively:', error);

          // If refresh fails, redirect to login
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: handle 401 errors by refreshing token
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // If error is 401 and we haven't retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip retry for auth endpoints
        if (
          originalRequest.url?.includes('/auth/otp/request') ||
          originalRequest.url?.includes('/auth/otp/verify') ||
          originalRequest.url?.includes('/auth/token/refresh') ||
          originalRequest.url?.includes('/auth/anonymous/session')
        ) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          // No refresh token - clear session and redirect to login
          console.warn('No refresh token available - redirecting to login');
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject });
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axiosInstance(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          console.log('Received 401 - attempting token refresh');
          await authService.refreshToken();
          const newToken = localStorage.getItem('accessToken');

          if (!newToken) {
            throw new Error('Failed to get new token after refresh');
          }

          isRefreshing = false;
          processQueue(null, newToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);

          // Refresh failed - clear session and redirect to login
          console.error('Token refresh failed - redirecting to login');
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
